import { SprintsCarousel } from "#/features/sprints-landing/carousel"
import { DesignedBy } from "#/features/sprints-landing/designed-by"
import {
  DownloadButtons,
  SPRINTS_APP_STORE_URL,
  SPRINTS_GOOGLE_PLAY_URL
} from "#/features/sprints-landing/download-buttons"
import { GettingStartedList } from "#/features/sprints-landing/getting-started"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import {
  Box,
  Button,
  Divider,
  Drawer,
  InfoIcon,
  InternalIcon,
  LaptopIcon,
  Link,
  Tile,
  Typography,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import React, { useEffect, useState } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { ContentFilters as SprintsContentFilters } from "#/features/sprints-landing/carousel-sections"
import { Form, FormButton, FormCheckbox } from "@carrotfertility/carotene-core-x"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { Title } from "#/services/page-title/Title"
import sprintsLogoSrc from "#/features/sprints-landing/assets/images/sprints-logo.png"
import Settings from "#/utils/CarrotConfig"
import { QRCodeSVG } from "qrcode.react"
import { isIOSNativeApp, isMobileApp } from "#/lib/carrot-api/MobileNativeClient"
import { SPRINTS_MESSAGES } from "#/features/sprints-landing/en-only-text"
import Markdown from "markdown-to-jsx"
import { FormattedMessage, useIntl } from "react-intl"
import { useFlags } from "launchdarkly-react-client-sdk"
import { DbgPaths } from "#/utils/Paths"

function useContentFilters() {
  const { data: benefit, isLoading } = useBenefit()
  const [contentFilters, setContentFilters] = useState<SprintsContentFilters>({
    showGLP1Content: false,
    showTelemedicineContent: false
  })

  useEffect(() => {
    if (!isLoading) {
      setContentFilters({
        showGLP1Content: benefit?.sprints?.fertilityCare?.telemedicine?.hasEligibleExpenses, // GLP-1s are the only eligible expense covered by Telemedicine
        showTelemedicineContent: benefit?.sprints?.fertilityCare?.telemedicine?.enabled
      })
    }
  }, [benefit, isLoading])

  return {
    contentFilters,
    setContentFilters
  }
}

/**
 * Temporary tools to allow for testing dynamic content based on company configuration.
 * These are a temporary solution and should be removed once the dynamic content tool checkbox is fully implemented.
 */
function ContentFilterControls({
  contentFilters,
  setContentFilters
}: {
  contentFilters: SprintsContentFilters
  setContentFilters: (contentFilters: SprintsContentFilters) => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Box>
      <Button
        startIcon={<InfoIcon />}
        color="info"
        sx={{ position: "fixed", insetBlockEnd: "2rem", insetInlineEnd: "2rem" }}
        onClick={() => setIsOpen(true)}
      >
        {SPRINTS_MESSAGES.testingTools.button}
      </Button>
      <Drawer anchor="bottom" open={isOpen} onClose={() => setIsOpen(false)}>
        <Box sx={(theme) => ({ padding: theme.tokens.spacing.md })}>
          <Typography variant="h3" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
            {SPRINTS_MESSAGES.testingTools.title}
          </Typography>
          <Form
            defaultValues={contentFilters}
            onSubmit={(newContentFilters) => {
              setContentFilters({ ...contentFilters, ...newContentFilters })
              setIsOpen(false)
            }}
          >
            <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.md })}>
              <FormCheckbox label={SPRINTS_MESSAGES.testingTools.showTelemedicine} name="showTelemedicineContent" />
              <FormCheckbox label={SPRINTS_MESSAGES.testingTools.showGLP1} name="showGLP1Content" />
              <FormButton sx={{ alignSelf: "start" }} type="submit">
                {SPRINTS_MESSAGES.testingTools.update}
              </FormButton>
            </Box>
          </Form>
        </Box>
      </Drawer>
    </Box>
  )
}

export default function SprintIntroPage() {
  const currentUser = useCurrentUser()
  const { contentFilters, setContentFilters } = useContentFilters()
  const [showMobileAppCallToAction] = useState(isMobileApp())
  const { locale } = useIntl()
  const showLanguageSupportBanner = !locale.startsWith("en")
  const { sprintsJourneyExpansion } = useFlags()
  const benefitGuideLink = sprintsJourneyExpansion ? DbgPaths.FERTILITY_COVERAGE : DbgPaths.METABOLIC_FERTILITY_HEALTH

  return (
    <PageLayout
      sx={{ maxInlineSize: 670 }}
      header={
        <PageHeader
          startGlyph={<LaptopIcon sx={{ blockSize: "2.25rem", inlineSize: "2.25rem" }} />}
          pageTitle={SPRINTS_MESSAGES.pageTitle}
        />
      }
    >
      <Title title={SPRINTS_MESSAGES.tabTitle} />
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.xl)
        })}
      >
        {showLanguageSupportBanner && <LanguageSupportBanner />}
        <Section>
          <Tile
            variant="primary"
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(theme.tokens.spacing.md)
            })}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: theme.spacing(theme.tokens.spacing.md),
                alignItems: "end"
              })}
            >
              {sprintsLogo}
              <Typography variant="h5">{SPRINTS_MESSAGES.introduction.title}</Typography>
            </Box>
            <Typography variant="body1">{SPRINTS_MESSAGES.introduction.description}</Typography>
            <Box>
              <Typography variant="body2">{SPRINTS_MESSAGES.introduction.availability}</Typography>
              <DownloadButtonsWithQRCode />
            </Box>
          </Tile>
          <Typography variant="body1">{SPRINTS_MESSAGES.introduction.clinicalBacking}</Typography>
          <Typography variant="body1">{SPRINTS_MESSAGES.introduction.goalDescription}</Typography>
          {showMobileAppCallToAction && <MobileAppCallToAction />}
        </Section>
        <Divider />
        <Section>
          <Typography variant="h3">{SPRINTS_MESSAGES.howItHelps.title}</Typography>
          <Typography variant="body1">{SPRINTS_MESSAGES.howItHelps.description}</Typography>
          <SprintsCarousel contentFilters={contentFilters} />
        </Section>
        <Section>
          <Typography variant="h3">{SPRINTS_MESSAGES.whyItMatters.title}</Typography>
          <Typography variant="body1">{SPRINTS_MESSAGES.whyItMatters.description}</Typography>
          <GettingStartedList />
        </Section>
        <Section>
          <Box
            sx={(theme) => ({
              paddingBlockEnd: theme.tokens.spacing.lg,
              display: "flex",
              flexDirection: "column",
              gap: theme.tokens.spacing.xl
            })}
          >
            {showMobileAppCallToAction ? (
              <MobileAppCallToAction />
            ) : (
              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "row",
                  gap: theme.tokens.spacing.sm
                })}
              >
                <DownloadButtons />
              </Box>
            )}
            {contentFilters.showTelemedicineContent && (
              <Tile
                variant="primary"
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.tokens.spacing.md
                })}
              >
                {glp1AccessGlyph}
                <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.xs })}>
                  <Typography variant="h5">{SPRINTS_MESSAGES.telemedicine.title}</Typography>
                  <Typography variant="body1" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
                    <Markdown>{SPRINTS_MESSAGES.telemedicine.description}</Markdown>
                  </Typography>
                  {contentFilters.showGLP1Content ? (
                    <Typography variant="body2" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
                      <Markdown
                        options={{
                          overrides: {
                            link: {
                              component: (props) => (
                                <Link
                                  sx={{ color: "inherit", fontSize: "inherit" }}
                                  component={ReactRouterLink}
                                  to={benefitGuideLink}
                                  color="inherit"
                                >
                                  {props.children}
                                </Link>
                              )
                            }
                          }
                        }}
                      >
                        {SPRINTS_MESSAGES.telemedicine.glp1CoverageNote}
                      </Markdown>
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.md })}>
                      <Markdown
                        options={{
                          overrides: {
                            link: {
                              component: (props) => (
                                <Link
                                  sx={{ color: "inherit", fontSize: "inherit" }}
                                  component={ReactRouterLink}
                                  to={benefitGuideLink}
                                  color="inherit"
                                >
                                  {props.children}
                                </Link>
                              )
                            }
                          }
                        }}
                      >
                        {SPRINTS_MESSAGES.telemedicine.insuranceCoverageNote}
                      </Markdown>
                    </Typography>
                  )}
                </Box>
              </Tile>
            )}
          </Box>
        </Section>
        <Section>
          <DesignedBy />
        </Section>
        <Section>
          <Tile
            variant="primary"
            color="blue"
            sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.lg })}
          >
            <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.tokens.spacing.md })}>
              {carrotMobileGlyph}
              <Typography variant="h3">{SPRINTS_MESSAGES.callToAction.title}</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1">{SPRINTS_MESSAGES.callToAction.description}</Typography>
              {showMobileAppCallToAction ? (
                <Box sx={{ paddingBlockStart: (theme) => theme.tokens.spacing.lg }}>
                  <MobileAppCallToAction />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.md })}>
                    {SPRINTS_MESSAGES.callToAction.availability}
                  </Typography>
                  <DownloadButtonsWithQRCode />
                </>
              )}
            </Box>
            {!sprintsJourneyExpansion && (
              <Link
                component={ReactRouterLink}
                to={benefitGuideLink}
                textAlign="start"
                endIcon={<InternalIcon color="inherit" fontSize="small" />}
              >
                {SPRINTS_MESSAGES.callToAction.learnMore}
              </Link>
            )}
          </Tile>
        </Section>
      </Box>

      {currentUser?.isTestUser && (
        <ContentFilterControls contentFilters={contentFilters} setContentFilters={setContentFilters} />
      )}
    </PageLayout>
  )
}

const sprintsLogo = (
  <Box
    component="img"
    src={sprintsLogoSrc}
    alt=""
    sx={(theme) => ({
      blockSize: 55,
      inlineSize: 55,
      borderRadius: theme.tokens.borderRadius.md
    })}
  />
)

const glp1AccessGlyph = (
  <Box
    component="img"
    src="/images/icon-doctor.svg"
    alt=""
    sx={(theme) => ({
      blockSize: 60,
      inlineSize: 60,
      padding: theme.tokens.spacing.xxs,
      borderRadius: theme.tokens.borderRadius.md
    })}
  />
)

const carrotMobileGlyph = (
  <Box
    component="img"
    src="/images/icon-mobile.svg"
    alt=""
    sx={(theme) => ({
      blockSize: 60,
      inlineSize: 60,
      padding: theme.tokens.spacing.xxs,
      borderRadius: theme.tokens.borderRadius.md
    })}
  />
)

function MobileAppCallToAction() {
  const [callToActionUrl] = useState(() => {
    const isIOS = isIOSNativeApp()
    return isIOS ? SPRINTS_APP_STORE_URL : SPRINTS_GOOGLE_PLAY_URL
  })
  return (
    <Button component="a" href={callToActionUrl} sx={{ inlineSize: "fit-content" }}>
      {SPRINTS_MESSAGES.callToAction.getStarted}
    </Button>
  )
}

function DownloadButtonsWithQRCode() {
  const theme = useTheme()
  const showQRCode = useMediaQuery(theme.breakpoints.up("md"))
  const qrCodeUrl = `${Settings.CARROT_BACKEND_URL}/api/v2/sprints/download`
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "self-end"
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          gap: theme.tokens.spacing.sm,
          paddingBlockStart: (theme) => theme.tokens.spacing.lg
        })}
      >
        <DownloadButtons />
      </Box>
      {showQRCode && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: theme.tokens.spacing.sm,
            bgcolor: "white",
            blockSize: 120,
            inlineSize: 120,
            padding: (theme) => theme.tokens.spacing.xs,
            alignItems: "center",
            boxSizing: "border-box"
          }}
        >
          <QRCodeSVG value={qrCodeUrl} />
        </Box>
      )}
    </Box>
  )
}

const LanguageSupportBanner = () => (
  <Box
    sx={(theme) => ({
      bgcolor: theme.palette.background.default,
      textAlign: "center",
      borderRadius: theme.tokens.borderRadius.sm,
      padding: theme.spacing(theme.tokens.spacing.xxxs, theme.tokens.spacing.xs)
    })}
  >
    <Typography variant="body1">
      <FormattedMessage defaultMessage="Please note: The Sprints app is only available in English at this time" />
    </Typography>
  </Box>
)

function Section({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="section"
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(theme.tokens.spacing.lg)
      })}
    >
      {children}
    </Box>
  )
}
